import { isVolumeManaged } from '../classification';
import { formatBytes, volumeHasFriendlyName } from '../formatBytes';
import type { ExplorationVolume } from '../../types/exploration';
import { infraBubbleStyles as s } from '../../components/BubbleCanvas/bubbleDimColors';
import { managedSizeArrowRowWidth } from './sizeArrowIcon';

/** Structured size for bubble labels — range uses SVG arrow between values. */
export type VolumeBubbleSizeDisplay =
  | { kind: 'single'; text: string }
  | { kind: 'range'; original: string; current: string };

/** Size line inside a volume bubble; managed volumes show original → current via SVG arrow. */
export function volumeBubbleSizeDisplay(v: ExplorationVolume): VolumeBubbleSizeDisplay {
  const managed = isVolumeManaged(v);
  const current = v.datafyDiskSize;
  if (managed && current != null && current !== v.size) {
    return { kind: 'range', original: formatBytes(v.size), current: formatBytes(current) };
  }
  return { kind: 'single', text: formatBytes(v.size) };
}

/**
 * Plain-text size for tests / a11y fallbacks.
 * Canvas renders the range with an SVG arrow; this keeps a Unicode arrow for strings.
 */
export function volumeBubbleSizeLabel(v: ExplorationVolume): string {
  const display = volumeBubbleSizeDisplay(v);
  if (display.kind === 'range') {
    return `${display.original} → ${display.current}`;
  }
  return display.text;
}

export interface VolumeBubbleTextLine {
  key: 'name' | 'id' | 'size';
  text: string;
  /** When set, size line renders original + vector.svg arrow + current. */
  sizeArrow?: { original: string; current: string };
  fontSize: number;
  lineHeight: number;
  fontWeight: number;
  fill: string;
  tabular?: boolean;
}

export const volumeBubbleLabelLayout = {
  /** Gap between stacked label lines. */
  labelGap: 2,
  /** Extra space between the volume icon and the first text line. */
  iconTextGap: 4,
  /** Inset from the bubble circle edge so text does not touch the stroke. */
  textEdgePad: 2,
  /** Hide name/id text when fewer than this many characters fit the chord. */
  minLabelChars: 6,
} as const;

const LINE_HEIGHT_BY_FONT_SIZE: Record<number, number> = {
  12: 16,
  11: 14,
};

function lineHeightForFontSize(fontSize: number): number {
  return LINE_HEIGHT_BY_FONT_SIZE[fontSize] ?? fontSize * 1.15;
}

/** Horizontal chord width available for text at a vertical offset from bubble center. */
export function volumeLabelChordWidthPx(
  screenR: number,
  dyFromCenter: number,
  edgePad = volumeBubbleLabelLayout.textEdgePad
): number {
  const ay = Math.abs(dyFromCenter);
  if (ay >= screenR) return 0;
  return Math.max(0, 2 * Math.sqrt(screenR * screenR - ay * ay) - 2 * edgePad);
}

/** Truncate name/id text to fit a chord (ellipsis). */
export function fitVolumeLabelText(full: string, charW: number, chordPx: number): string {
  const { minLabelChars } = volumeBubbleLabelLayout;
  if (!full || chordPx < charW * minLabelChars) return '';
  const maxChars = Math.max(minLabelChars, Math.floor(chordPx / charW));
  return full.length <= maxChars ? full : full.slice(0, maxChars - 1) + '…';
}

/** True when the full string fits the chord without truncation. */
export function volumeLabelFullLineFits(full: string, charW: number, chordPx: number): boolean {
  if (!full || chordPx <= 0) return false;
  return full.length * charW <= chordPx;
}

/** Whether a managed size row (original + arrow + current) fits the chord. */
export function sizeArrowRowFits(
  sizeArrow: { original: string; current: string },
  charW: number,
  chordPx: number,
  measuredWidth?: number
): boolean {
  if (chordPx <= 0) return false;
  const w = measuredWidth ?? managedSizeArrowRowWidth(sizeArrow.original, sizeArrow.current, charW);
  return w <= chordPx;
}

/**
 * Display text for a volume label line: name/id may ellipsize; plain size is all-or-nothing.
 * Managed size-arrow rows use `sizeArrowRowFits` instead.
 */
export function resolveVolumeLabelDisplayText(
  line: Pick<VolumeBubbleTextLine, 'key' | 'text' | 'sizeArrow'>,
  charW: number,
  chordPx: number
): string {
  if (line.key === 'size') {
    if (line.sizeArrow) return '';
    return volumeLabelFullLineFits(line.text, charW, chordPx) ? line.text : '';
  }
  return fitVolumeLabelText(line.text, charW, chordPx);
}

export interface VolumeBubbleRowSelection {
  showIcon: boolean;
  visible: VolumeBubbleTextLine[];
}

/**
 * Progressive row drop for a volume bubble running out of vertical room.
 * Priority to keep, lowest first: Volume ID, Name, Icon, Size — so rows are
 * dropped cumulatively in that order (ID first) until `fits` passes, with
 * Size the last thing ever hidden.
 */
export function selectVisibleVolumeBubbleRows(
  lines: VolumeBubbleTextLine[],
  hasIcon: boolean,
  fits: (candidate: VolumeBubbleRowSelection) => boolean
): VolumeBubbleRowSelection {
  const dropOrder: Array<'id' | 'name' | 'icon'> = ['id', 'name', 'icon'];
  const droppable = dropOrder.filter((key) =>
    key === 'icon' ? hasIcon : lines.some((l) => l.key === key)
  );

  for (let i = 0; i <= droppable.length; i++) {
    const dropped = new Set(droppable.slice(0, i));
    const candidate: VolumeBubbleRowSelection = {
      showIcon: hasIcon && !dropped.has('icon'),
      visible: lines.filter((l) => l.key === 'size' || !dropped.has(l.key as 'id' | 'name')),
    };
    if (fits(candidate)) return candidate;
  }

  return { showIcon: false, visible: [] };
}

/** Ordered text lines for a volume bubble label stack (icon rendered separately). */
export function volumeBubbleTextLines(v: ExplorationVolume): VolumeBubbleTextLine[] {
  const managed = isVolumeManaged(v);
  const fillBold = managed ? s.chip.text.name : s.chip.text.nameUnmanaged;
  const fillReg = s.chip.text.size;
  const sizeDisplay = volumeBubbleSizeDisplay(v);

  const lines: VolumeBubbleTextLine[] = [];

  if (volumeHasFriendlyName(v)) {
    lines.push({
      key: 'name',
      text: v.name.trim(),
      fontSize: 12,
      lineHeight: lineHeightForFontSize(12),
      fontWeight: 500,
      fill: fillBold,
    });
  }

  const sizeLine: VolumeBubbleTextLine = {
    key: 'size',
    text: sizeDisplay.kind === 'range' ? volumeBubbleSizeLabel(v) : sizeDisplay.text,
    ...(sizeDisplay.kind === 'range'
      ? { sizeArrow: { original: sizeDisplay.original, current: sizeDisplay.current } }
      : {}),
    fontSize: 11,
    lineHeight: lineHeightForFontSize(11),
    fontWeight: 400,
    fill: fillReg,
    tabular: true,
  };

  lines.push(
    {
      key: 'id',
      text: v.ebsId,
      fontSize: 11,
      lineHeight: lineHeightForFontSize(11),
      fontWeight: 400,
      fill: fillReg,
    },
    sizeLine
  );

  return lines;
}
